import type { DialogProps } from '@headlessui/react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type React from 'react';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import FormSubmitButton from '@/components/Forms/FormSubmitButton';
import { Button, type ButtonProps } from '@/components/UI/Button';

interface ButtonsConfig {
  cancel?: ButtonProps | React.ReactNode | false;
  submit?: React.ComponentProps<typeof FormSubmitButton> | React.ReactNode | true;
}

export type ButtonCallback<T> = (cfg: T, dflt: React.ReactNode) => React.ReactNode;

type NonArray = object & { [Symbol.iterator]?: never };

function isNotReact<T extends NonArray>(obj: T | React.ReactNode): obj is T {
  if (typeof obj === 'object') {
    if (Array.isArray(obj)) {
      return false;
    }

    if (
      typeof (obj as React.ReactElement)?.type !== 'undefined' &&
      typeof (obj as React.ReactElement)?.props !== 'undefined' &&
      typeof (obj as React.ReactElement)?.key !== 'undefined'
    ) {
      return false;
    }

    return true;
  }

  return false;
}

const BLANKOBJ = {};

/** If `cfg` is a config object, returns `callback` result value and returns the value, or default if no callback.
 * If `cfg` is undefined or `true`, returns default.
 * If `cfg` is non-null (eg. ReactNode), returns cfg.
 * In any other case, returns default.
 */
function jsxFromConfig<T extends NonArray>(
  cfg: T | React.ReactNode,
  dflt: React.ReactNode,
  callback?: ButtonCallback<T>,
) {
  if (cfg === undefined || (cfg === true && dflt)) {
    return dflt;
  }

  if (cfg === true) {
    return callback ? callback(BLANKOBJ as T, dflt) : dflt;
  }

  if (cfg === false || cfg === null) {
    return null;
  }

  if (isNotReact<T>(cfg)) {
    return callback ? callback(cfg, dflt) : dflt;
  }

  return cfg || dflt;
}

const defaultOnIsOpenChange = (_to: boolean, why: string) => {
  if (why === 'dialog-onclose') {
    return false;
  }

  return true;
};

const noopFalse = () => false;
const noopTrue = () => false;

export default function Modal<D extends React.ElementType>({
  blur = true,
  buttonsClassName,
  buttons,
  children,
  icon,
  modalProps,
  onIsOpenChange: onIsOpenChangeBase = defaultOnIsOpenChange,
  title,
  isOpen: isOpenBase,
  isOpenInitial = true,
  setIsOpen: setIsOpenBase,
  titleProps,
}: {
  blur?: boolean;
  buttonsClassName?: HTMLButtonElement['className'];
  buttons?: ButtonsConfig | React.ReactNode;
  children:
    | React.ReactNode
    | ((
        isOpen: boolean,
        setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
      ) => React.ReactNode);
  icon?: React.ReactNode | 'exclamation';
  modalProps?: Omit<DialogProps<D>, 'open' | 'onClose' | 'className'> & {
    className?: string | null;
  };
  /** Control if the open status should change by internal callbacks */
  onIsOpenChange?: boolean | ((newValue: boolean, why: string) => boolean);
  titleClassName?: HTMLDivElement['className'];
  titleProps?: React.ComponentProps<typeof DialogTitle>;
  title?: React.ReactNode;
  /** If passed, state is no longer internally managed */
  isOpen?: boolean;
  /** Starting state of isOpen if using internally managed state */
  isOpenInitial?: boolean;
  /** If passed, you must also give `isOpen` */
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [isOpenInner, setIsOpenInner] = useState(isOpenInitial);

  const isOpen = isOpenBase === undefined ? isOpenInner : isOpenBase;
  const setIsOpen = setIsOpenBase === undefined ? setIsOpenInner : setIsOpenBase;
  const onIsOpenChange =
    onIsOpenChangeBase === true
      ? noopTrue
      : onIsOpenChangeBase === false
        ? noopFalse
        : onIsOpenChangeBase;

  const defaultCancelButton = (
    <Button
      variant="outline"
      onClick={() => onIsOpenChange(false, 'cancel-button') && setIsOpen(false)}
    >
      {isNotReact(buttons) && buttons.submit ? 'Cancel' : 'Close'}
    </Button>
  );

  return (
    <Dialog
      as="aside"
      {...modalProps}
      className={twMerge('relative z-60', modalProps?.className)}
      open={isOpen}
      onClose={(value) => onIsOpenChange(value, 'dialog-onclose') && setIsOpen(value)}
    >
      <DialogBackdrop
        transition
        className={twMerge(
          'fixed inset-0 bg-zinc-800/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-leave:duration-200 data-enter:ease-out data-leave:ease-in',
          blur && 'backdrop-blur-xs',
        )}
      />

      <div className="fixed inset-0 z-60 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative overflow-hidden rounded-lg bg-[Canvas] text-left text-[CanvasText] shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-leave:duration-200 data-enter:ease-out data-leave:ease-in sm:my-8 sm:max-w-lg sm:data-closed:translate-y-0 sm:data-closed:scale-95"
          >
            <section className="px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                {!!icon && (
                  <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-zinc-100 sm:mx-0 sm:size-10 dark:bg-zinc-900">
                    {icon === 'exclamation' ? (
                      <ExclamationTriangleIcon
                        height={24}
                        className="size-6 text-zinc-600"
                        aria-hidden="true"
                      />
                    ) : (
                      icon
                    )}
                  </div>
                )}
                <article className="mt-3 text-center text-sm sm:ml-4 sm:mt-0 sm:text-left">
                  {!!title && (
                    <DialogTitle
                      as="h3"
                      {...titleProps}
                      className={twMerge(
                        'mb-2 text-base font-semibold leading-6',
                        titleProps?.className,
                      )}
                    >
                      {title}
                    </DialogTitle>
                  )}
                  {!!children &&
                    (typeof children === 'function' ? children(isOpen, setIsOpen) : children)}
                </article>
              </div>
            </section>
            <nav
              className={twMerge(
                'px-3 py-3 sm:flex sm:px-6',
                buttonsClassName || 'gap-x-3 sm:flex-row-reverse',
              )}
            >
              {jsxFromConfig(buttons, defaultCancelButton, (cfg) => (
                <>
                  {jsxFromConfig(cfg.submit, null, (btnCfg) => (
                    <FormSubmitButton type="submit" inline={true} stretch={false} {...btnCfg}>
                      {btnCfg.children || 'Submit'}
                    </FormSubmitButton>
                  ))}

                  {jsxFromConfig(cfg.cancel, defaultCancelButton, (btnCfg) => (
                    <Button
                      variant="outline"
                      {...btnCfg}
                      onClick={
                        btnCfg.onClick ||
                        (() => onIsOpenChange(false, 'cancel-button') && setIsOpen(false))
                      }
                    >
                      {btnCfg.children ||
                        (isNotReact(buttons) && buttons.submit ? 'Cancel' : 'Close')}
                    </Button>
                  ))}
                </>
              ))}
            </nav>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
